import React from "react";
import { Phone, MessageCircle, HeartPulse, Siren, Clock } from "lucide-react";
import { CIMA, openWhatsapp } from "@/lib/cima";

/**
 * Strip / banner that highlights 24/7 telephone attention for medical oxygen
 * and medical emergencies. Designed to draw immediate attention without
 * cannibalising the hero. Positioned right above the promo banner.
 */
export default function Emergency24Banner() {
  const callMsg =
    "Hola CIMA, necesito una recarga de oxígeno medicinal urgente.";

  return (
    <section
      data-testid="emergency-24-banner"
      aria-label="Atención telefónica 24/7 para recargas y soporte de oxígeno medicinal"
      className="relative bg-white border-y-2 border-[var(--cima-red)] overflow-hidden"
    >
      {/* Pulse dot decoration left */}
      <div className="absolute -left-12 top-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-[var(--cima-red)]/5 blur-2xl pointer-events-none" />
      <div className="absolute -right-12 top-1/2 -translate-y-1/2 w-40 h-40 rounded-full bg-[var(--cima-navy)]/5 blur-2xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 lg:py-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
          {/* Pulsing 24/7 badge */}
          <div className="flex items-center gap-3 shrink-0">
            <div className="relative w-12 h-12 lg:w-14 lg:h-14 bg-[var(--cima-red)] text-white flex items-center justify-center flex-shrink-0">
              <Siren className="w-6 h-6 lg:w-7 lg:h-7" strokeWidth={2} />
              <span className="absolute inset-0 border-2 border-[var(--cima-red)] animate-ping rounded-none opacity-60" />
            </div>
            <div className="flex flex-col">
              <span className="font-mono-cima text-[10px] uppercase tracking-[0.3em] text-[var(--cima-red)] font-bold flex items-center gap-1.5">
                <Clock className="w-3 h-3" />
                Atención telefónica 24/7
              </span>
              <span className="font-display font-extrabold text-lg lg:text-2xl text-[var(--cima-navy)] leading-tight">
                Recargas urgentes &amp; Oxígeno medicinal
              </span>
            </div>
          </div>

          {/* Vertical divider on desktop */}
          <div className="hidden lg:block w-px h-12 bg-slate-200" />

          {/* Description */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-slate-600 leading-snug">
              <HeartPulse className="inline w-4 h-4 text-[var(--cima-red)] mr-1 -mt-0.5" />
              Estamos disponibles las <strong className="text-[var(--cima-navy)]">24 horas, los 365 días</strong> para recargas de oxígeno medicinal y soporte a pacientes en Zihuatanejo y zona conurbada.
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <a
              data-testid="emergency-call-button"
              href={`tel:${CIMA.emergency24Tel}`}
              className="inline-flex items-center justify-center gap-2 bg-[var(--cima-red)] hover:bg-[var(--cima-red-deep)] text-white px-5 py-3 font-display font-bold text-sm uppercase tracking-wide transition-colors"
            >
              <Phone className="w-4 h-4" />
              Llamar {CIMA.phone}
            </a>
            <button
              type="button"
              data-testid="emergency-whatsapp-button"
              onClick={() => openWhatsapp(callMsg)}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[var(--cima-navy)] hover:text-white text-[var(--cima-navy)] border-2 border-[var(--cima-navy)] px-5 py-3 font-display font-bold text-sm uppercase tracking-wide transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
